// Enriched dashboard tier (§5.2): genre distribution, top directors, and hot
// takes. Runs the TMDB library sweep in the background and renders progressively
// (skeleton -> populated), persisting via the IndexedDB cache.

import type { EnrichedStats as Stats } from "../lib/enrich.ts";
import { Analyzing } from "./Analyzing.tsx";

/** Presentational — the sweep is driven by useEnrichedStats one level up. */
export function EnrichedStats({ stats, progress }: { stats: Stats | null; progress: { done: number; total: number } }) {
  if (!stats) {
    return <Analyzing done={progress.done} total={progress.total} />;
  }

  const maxGenre = Math.max(1, ...stats.genres.map((g) => g.count));

  return (
    <div data-testid="enriched-stats" className="mt-3 space-y-6">
      {/* Genres */}
      <div>
        <h4 className="mb-2 text-sm font-semibold text-ink-900">Genres</h4>
        <div className="space-y-1.5">
          {stats.genres.slice(0, 6).map((g) => (
            <div key={g.name} className="flex items-center gap-2 text-sm">
              <span className="w-20 shrink-0 text-ink-400">{g.name}</span>
              <div className="h-3 flex-1 rounded bg-paper-100">
                <div className="h-full rounded bg-red-600" style={{ width: `${(g.count / maxGenre) * 100}%` }} />
              </div>
              <span className="w-8 text-right text-ink-400 tabular-nums">{g.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Directors */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <h4 className="mb-2 text-sm font-semibold text-ink-900">Most-watched directors</h4>
          <ul className="space-y-1 text-sm">
            {stats.topDirectorsByCount.map((d) => (
              <li key={d.name} className="flex justify-between">
                <span className="text-ink-900">{d.name}</span>
                <span className="text-ink-400 tabular-nums">{d.count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="mb-2 text-sm font-semibold text-ink-900">Highest-rated directors</h4>
          <ul className="space-y-1 text-sm">
            {stats.topDirectorsByRating.length ? (
              stats.topDirectorsByRating.map((d) => (
                <li key={d.name} className="flex justify-between">
                  <span className="text-ink-900">{d.name}</span>
                  <span className="text-ink-400 tabular-nums">★{d.avgRating.toFixed(1)}</span>
                </li>
              ))
            ) : (
              <li className="text-ink-400">Not enough repeat directors yet.</li>
            )}
          </ul>
        </div>
      </div>

      {/* Hot takes */}
      <div className="grid gap-4 sm:grid-cols-2" data-testid="hot-takes">
        <HotTakeList title="You loved it, everyone else shrugged" items={stats.lovedByYouOnly} tone="orange" />
        <HotTakeList title="Everyone loves it, you don't get it" items={stats.lovedByWorldOnly} tone="teal" />
      </div>
    </div>
  );
}

function HotTakeList({
  title,
  items,
  tone,
}: {
  title: string;
  items: { uri: string; title: string; year?: number; userRating: number; worldRating: number }[];
  tone: "orange" | "teal";
}) {
  const color = tone === "orange" ? "text-gold-500" : "text-green-500";
  return (
    <div>
      <h4 className="mb-2 text-sm font-semibold text-ink-900">{title}</h4>
      {items.length ? (
        <ul className="space-y-1.5 text-sm">
          {items.map((h) => (
            <li key={h.uri} className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate text-ink-900">{h.title}</span>
              <span className="shrink-0 tabular-nums text-ink-400">
                <span className={color}>★{h.userRating.toFixed(1)}</span> vs ★{h.worldRating.toFixed(1)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-ink-400">No strong disagreements here.</p>
      )}
    </div>
  );
}
